/**
 * Export Utilities
 *
 * Functions for exporting data to CSV and triggering file downloads.
 * Extracted from base.html exportCampaignEmails function.
 */

/**
 * Export data to CSV format
 * @param {Array<Object>} data - Array of objects to export
 * @param {string} filename - Filename without extension
 * @param {string[]} [columns] - Column order (defaults to object keys)
 */
export function exportToCSV(data, filename, columns = null) {
  if (!data || data.length === 0) {
    console.warn('[Export] No data to export');
    return;
  }

  // Determine columns from first row if not specified
  const cols = columns || Object.keys(data[0]);

  // Create header row
  const headerRow = cols.map(escapeCSVValue).join(',');

  // Create data rows
  const dataRows = data.map(row => {
    return cols.map(col => escapeCSVValue(row[col])).join(',');
  });

  // Combine into CSV string
  const csvContent = [headerRow, ...dataRows].join('\n');

  // Create blob and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, `${filename}.csv`);
}

/**
 * Escape a value for CSV format
 * @param {any} value
 * @returns {string}
 */
function escapeCSVValue(value) {
  if (value === null || value === undefined) {
    return '';
  }

  const stringValue = String(value);

  // If contains comma, newline, or quote, wrap in quotes and escape existing quotes
  if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
}

/**
 * Download a blob as a file
 * @param {Blob} blob
 * @param {string} filename
 */
export function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Download file from URL
 * @param {string} url - URL to download from
 * @param {string} filename - Filename for download
 * @returns {Promise<void>}
 */
export async function downloadFile(url, filename) {
  try {
    const response = await fetch(url, {
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const blob = await response.blob();
    downloadBlob(blob, filename);

  } catch (error) {
    console.error('[Export] Download failed:', error);
    throw error;
  }
}

/**
 * Export campaign emails (backward compatible function)
 * This matches the original function signature from base.html
 * @param {string|number} campaignId
 * @param {string} exportUrl
 * @returns {Promise<void>}
 */
export async function exportCampaignEmails(campaignId, exportUrl) {
  try {
    await downloadFile(exportUrl, `campaign_${campaignId}_emails.csv`);
  } catch (error) {
    console.error('[Export] Failed to export campaign emails:', error);
    alert('Failed to export emails. Please try again.');
  }
}

// Expose for backward compatibility
window.exportCampaignEmails = exportCampaignEmails;

export default {
  exportToCSV,
  downloadBlob,
  downloadFile,
  exportCampaignEmails,
};
